import { useEffect, useState } from "react";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import MacBookAnimation from "@/components/MacBookAnimation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Handshake, TrendingUp, Users, Mail, ExternalLink, Send, Loader2, Star, Zap, Edit3 } from "lucide-react";
import { toast } from "sonner";

interface Partner {
  id: string;
  name: string;
  type: string;
  status: string;
  official_url: string;
  referral_url: string;
  commission_per_referral_eur: number;
  commission_currency: string;
  decision_maker_role: string;
  use_case: string;
  logo_emoji: string;
  deals_count: number;
  commission_total_eur: number;
  commission_en_attente_eur?: number;
  primes?: { id: string; client: string; montant_ht_eur: number; statut: string; date_signature?: string; date_paiement?: string; rapproche_banque?: boolean; entite_facturation?: string }[];
  interactions: { id: string; type: string; message: string; at: string }[];
}

interface Stats {
  total_partners: number;
  active_partners: number;
  total_deals: number;
  total_commissions_eur: number;
  total_en_attente_eur?: number;
  par_entite?: Record<string, { encaisse_eur: number; en_attente_eur: number }>;
}

const STATUS_META: Record<string, string> = {
  active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  in_discussion: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  prospect: "bg-muted text-muted-foreground border-border",
};

const STATUS_LABEL: Record<string, string> = {
  active: "Actif",
  in_discussion: "En discussion",
  prospect: "Prospect",
};

export default function PartnersDashboard() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [draftDialog, setDraftDialog] = useState<{ subject: string; body: string; partnerName: string } | null>(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/partners`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setPartners(d.partners || []);
        setStats(d.stats || null);
      }
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const generatePitch = async (p: Partner) => {
    setBusyId(p.id);
    try {
      const r = await fetch(`${BACKEND}/api/partners/${p.id}/generate-outreach`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("Génération impossible");
      const d = await r.json();
      setDraftDialog({ subject: d.subject, body: d.body, partnerName: p.name });
      toast.success(`Email préparé pour ${p.name}`);
      load();
    } catch (e) {
      toast.error("Erreur génération du pitch");
    } finally {
      setBusyId(null);
    }
  };

  const pushCowork = async (p: Partner) => {
    setBusyId(p.id);
    try {
      const r = await fetch(`${BACKEND}/api/partners/${p.id}/push-cowork`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("Push impossible");
      toast.success(`Mission Cowork → ${p.name} créée`);
      load();
    } catch (e) {
      toast.error("Erreur push Cowork");
    } finally {
      setBusyId(null);
    }
  };

  const updateStatus = async (p: Partner, status: string) => {
    try {
      const r = await fetch(`${BACKEND}/api/partners/${p.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (r.ok) {
        toast.success("Statut mis à jour");
        load();
      }
    } catch {
      /* noop */
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 p-6 md:p-10 overflow-y-auto">
          <div className="max-w-6xl mx-auto space-y-8">
            {/* Header */}
            <div>
              <h1 className="text-3xl font-bold tracking-tight" data-testid="partners-title">Relations Partenaires</h1>
              <p className="text-muted-foreground mt-1">
                Pilotez votre réseau d'apporteurs d'affaires et de parrainages avec Grafton IA
              </p>
            </div>

            <MacBookAnimation />

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card data-testid="partner-stat-total">
                <CardContent className="pt-5 flex items-center gap-3">
                  <Handshake className="h-6 w-6 text-primary" />
                  <div>
                    <div className="text-2xl font-bold">{stats?.total_partners ?? 0}</div>
                    <div className="text-xs text-muted-foreground">Partenaires</div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="partner-stat-active">
                <CardContent className="pt-5 flex items-center gap-3">
                  <Star className="h-6 w-6 text-emerald-400" />
                  <div>
                    <div className="text-2xl font-bold">{stats?.active_partners ?? 0}</div>
                    <div className="text-xs text-muted-foreground">Actifs</div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="partner-stat-deals">
                <CardContent className="pt-5 flex items-center gap-3">
                  <Users className="h-6 w-6 text-sky-400" />
                  <div>
                    <div className="text-2xl font-bold">{stats?.total_deals ?? 0}</div>
                    <div className="text-xs text-muted-foreground">Filleuls signés</div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="partner-stat-commissions">
                <CardContent className="pt-5 flex items-center gap-3">
                  <TrendingUp className="h-6 w-6 text-amber-400" />
                  <div>
                    <div className="text-2xl font-bold">
                      {(stats?.total_commissions_eur ?? 0).toLocaleString("fr-FR")} €
                    </div>
                    <div className="text-xs text-muted-foreground">Primes encaissées (HT)</div>
                    <div className="text-xs text-muted-foreground">{(stats?.total_en_attente_eur ?? 0).toLocaleString("fr-FR")} € en attente</div>
                    {Object.entries(stats?.par_entite || {}).map(([nom, v]) => (
                      <div key={nom} className="text-xs text-muted-foreground">{nom} : {v.encaisse_eur.toLocaleString("fr-FR")} € encaissés</div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Partners list */}
            {loading ? (
              <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="partners-list">
                {partners.map((p) => (
                  <Card key={p.id} className="border-border/60" data-testid={`partner-card-${p.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="text-3xl shrink-0">{p.logo_emoji || "🤝"}</div>
                          <div className="min-w-0">
                            <CardTitle className="text-lg truncate">{p.name}</CardTitle>
                            <p className="text-xs text-muted-foreground">{p.type}</p>
                          </div>
                        </div>
                        <Badge
                          className={STATUS_META[p.status] || STATUS_META.prospect}
                          variant="outline"
                          onClick={() => {
                            const next =
                              p.status === "prospect"
                                ? "in_discussion"
                                : p.status === "in_discussion"
                                  ? "active"
                                  : "prospect";
                            updateStatus(p, next);
                          }}
                          style={{ cursor: "pointer" }}
                          data-testid={`partner-status-${p.id}`}
                        >
                          {STATUS_LABEL[p.status] || p.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {p.use_case && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{p.use_case}</p>
                      )}

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-muted/40 rounded px-2 py-1.5">
                          <div className="text-muted-foreground">Commission</div>
                          <div className="font-semibold">
                            {p.commission_per_referral_eur > 0
                              ? `${p.commission_per_referral_eur} ${p.commission_currency}`
                              : "—"}
                          </div>
                        </div>
                        <div className="bg-muted/40 rounded px-2 py-1.5">
                          <div className="text-muted-foreground">Filleuls</div>
                          <div className="font-semibold">{p.deals_count}</div>
                        </div>
                      </div>
                      {(p.primes || []).length > 0 && (
                        <div className="space-y-1 text-xs">
                          {(p.primes || []).map((x) => (
                            <div key={x.id} className="flex items-center justify-between bg-muted/40 rounded px-2 py-1.5">
                              <span className="truncate pr-2">{x.client}{x.entite_facturation ? ` · ${x.entite_facturation}` : ""}</span>
                              <span className="whitespace-nowrap font-semibold">
                                {x.montant_ht_eur} € HT · {x.statut === "paye" ? (x.rapproche_banque ? "payée" : "payée (à rapprocher)") : x.statut === "facture" ? "facturée" : "signée"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => generatePitch(p)}
                          disabled={busyId === p.id}
                          data-testid={`partner-pitch-${p.id}`}
                          className="gap-1.5"
                        >
                          {busyId === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Mail className="h-3.5 w-3.5" />
                          )}
                          Mise en relation
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => pushCowork(p)}
                          disabled={busyId === p.id}
                          data-testid={`partner-cowork-${p.id}`}
                          className="gap-1.5"
                        >
                          <Zap className="h-3.5 w-3.5" /> Cowork
                        </Button>
                        {p.official_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            asChild
                            className="gap-1.5"
                            data-testid={`partner-website-${p.id}`}
                          >
                            <a href={p.official_url} target="_blank" rel="noreferrer">
                              <ExternalLink className="h-3.5 w-3.5" /> Site
                            </a>
                          </Button>
                        )}
                        {p.referral_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            asChild
                            className="gap-1.5"
                            data-testid={`partner-referral-${p.id}`}
                          >
                            <a href={p.referral_url} target="_blank" rel="noreferrer">
                              <Send className="h-3.5 w-3.5" /> Lien parrain
                            </a>
                          </Button>
                        )}
                      </div>

                      {p.interactions.length > 0 && (
                        <div className="text-[11px] text-muted-foreground pt-2 border-t border-border/40">
                          {p.interactions.length} interaction(s) ·{" "}
                          {p.interactions[p.interactions.length - 1].type.replace(/_/g, " ")}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Draft preview dialog */}
      <Dialog open={!!draftDialog} onOpenChange={() => setDraftDialog(null)}>
        <DialogContent className="max-w-2xl" data-testid="draft-preview-dialog">
          {draftDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit3 className="h-5 w-5 text-primary" />
                  Email préparé pour {draftDialog.partnerName}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Sujet</div>
                  <div className="font-medium">{draftDialog.subject}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Corps</div>
                  <div className="bg-muted/30 rounded p-3 text-sm whitespace-pre-wrap font-mono">
                    {draftDialog.body}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ce brouillon est sauvegardé dans vos actions agent en attente. Vous pouvez l'envoyer ou l'ajuster
                  depuis le dashboard.
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
